import { kqlEscape } from '../escape-utils'

import { Tool } from './base'
import {
  makeRenderer,
  normalizeList,
  PipeAndGroups,
  RenderContext,
  scalarToString,
} from './render/render-kit'
import { DataDisplayTemplate } from './render/rtd/datadisplay.types'
import { wrapRendererWithDataDisplay } from './render/rtd/datadisplay.utils'
import { NormalizedDataSource } from './render/rtd/datasource.types'
import {
  joinWithPipeBlock,
  wrapRendererWithDatasource,
} from './render/rtd/datasource.utils'
import { s1PowerQueryMap } from './render/rtd/tool-datasource-mappings/sentinelone-powerquery.datasource'
import { AstPredicate } from './render/types'

export const s1PowerQueryDataDisplayTemplate: DataDisplayTemplate = {
  projection: (fields) => `| columns ${fields.join(', ')}`,

  groupBy: (groupFields, aggClauses) => {
    const allClauses = [
      'eventCount=count()',
      'firstEvent=simpledateformat(min(timestamp))',
      'lastEvent=simpledateformat(max(timestamp))',
      ...aggClauses,
    ]
    return `| group ${allClauses.join(', ')} by ${groupFields.join(', ')}`
  },

  timeBucket: (span, groupFields, aggClauses) => {
    const allClauses = [
      'eventCount=count()',
      'firstEvent=simpledateformat(min(timestamp))',
      'lastEvent=simpledateformat(max(timestamp))',
      ...aggClauses,
    ]
    const byFields = [
      ...groupFields,
      `timeSpan=timebucket(timestamp,"${span}")`,
    ].join(', ')
    return `| group ${allClauses.join(', ')} by ${byFields}`
  },

  aggFns: {
    count: (_field, alias) => `${alias}=count()`,
    dcount: (field, alias) => `${alias}=estimate_distinct(${field})`,
    values: (field, alias) =>
      `${alias}=array_agg_distinct(${field}).to_string("\\n")`,
    collect: (field, alias) =>
      `${alias}=array_agg_distinct(${field}).to_string("\\n")`,
    first: (field, alias) => `${alias}=first(${field})`,
    last: (field, alias) => `${alias}=last(${field})`,
    min: (field, alias) => `${alias}=min(${field})`,
    max: (field, alias) => `${alias}=max(${field})`,
    sum: (field, alias) => `${alias}=sum(${field})`,
    avg: (field, alias) => `${alias}=avg(${field})`,
  },

  fieldAlias: null,

  join: (body, ddClause) => `${body}\n${ddClause}`,
}

export { s1PowerQueryMap }

export class SentinelOnePowerQueryTool extends Tool {
  public readonly toolName = 'SentinelOne - PowerQuery'
  public readonly toolKind = 'edr' as const

  public pivotEscape(key: string, value: string): string {
    return kqlEscape(value)
  }

  /** Notes and Implementation
   * Fields
   * - Map via toolTuningFields["sentinelone_powerquery"]; **no Eta** wrapping.
   *
   * Values & Lists
   * - Always **single-quoted**; escape \ and '.
   * - Multi-values: `('v1','v2',...)`.
   *
   * Logic & Grouping
   * - Uses PipeAndGroups
   * - Predicate: | filter
   * - Glue tokens: ` and ` / ` or ` (lowercase by config).
   * - Negation is a **logical wrapper**: `!( <clause> )`.
   *
   * Operators
   * - eq: single `F = 'v'`; multi `F in:anycase ('v1','v2')`.
   * - neq: single `!(F = 'v')`; multi `!(F in:anycase ('v1','v2'))`.
   * - contains / doesnotcontain: `F contains:anycase 'v'` / `!(F contains:anycase 'v')` (lists supported).
   * - startswith / endswith: `F matches:anycase '^value'` / `F matches:anycase 'value$'` (lists supported).
   * - Comparisons: `F < 'v'`, `<=`, `>`, `>=` (quoted).
   *
   * Null / Empty (confirmed)
   * - isnull → `!(F = *)`; isnotnull → `F = *`; isempty → `!(F = *)`; isnotempty → `F = *`.
   *
   * Safety
   * - Sanitize identifiers; use centralized single-quote `quote()`/`list()` helpers.
   */

  public renderTuning = makeRenderer({
    tool: this.toolName,
    groupStrategy: PipeAndGroups,
    renderContext: {
      // Single-quoted values with escaping for \ and '
      quote: (s: string) =>
        `'${String(s).replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`,
      // ('value1','value2','value3') using the same escaping
      list: (vals: string[]) =>
        `('${vals
          .map((v) => String(v).replace(/\\/g, '\\\\').replace(/'/g, "\\'"))
          .join("','")}')`,
      andGlue: ' and ',
      orGlue: ' or ',
      linePrefix: '| filter ',
      // No Eta wrapper for this tool:
      wrapField: (f) => f,
    },
    formatPredicate: (
      p: AstPredicate,
      renderContext: RenderContext,
    ): string => {
      const F = renderContext.wrapField(renderContext.mapField(p.field))
      const vals = normalizeList(p.value).map(scalarToString)

      // format "wildcarding" per value as needed
      const shaped = vals.map((s) => {
        switch (p.op) {
          case 'startswith':
            return `^${s}` // left-side anchor
          case 'endswith':
            return `${s}$` // right-side anchor
          // contains/doesnotcontain: no wildcarding needed
          default:
            return s // eq/neq and others unchanged
        }
      })
      const isMulti = shaped.length > 1

      const Q = (s: string) => renderContext.quote(s)
      const LIST = () => renderContext.list(shaped)

      // helpers for negation style: wrap final clause in !( ... )
      const NOT = (clause: string) => `!(${clause})`

      switch (p.op) {
        // equality / inequality
        case 'eq':
          return isMulti
            ? `${F} in:anycase ${LIST()}`
            : `${F} = ${Q(shaped[0])}`
        case 'neq':
          return isMulti
            ? NOT(`${F} in:anycase ${LIST()}`)
            : NOT(`${F} = ${Q(shaped[0])}`)

        // contains:anycase family (supports single or list)
        case 'contains':
          return isMulti
            ? `${F} contains:anycase ${LIST()}`
            : `${F} contains:anycase ${Q(shaped[0])}`
        case 'doesnotcontain':
          return isMulti
            ? NOT(`${F} contains:anycase ${LIST()}`)
            : NOT(`${F} contains:anycase ${Q(shaped[0])}`)

        // startswith/endswith are also expressed via contains:anycase + wildcard
        case 'startswith':
          return isMulti
            ? `${F} matches:anycase ${LIST()}`
            : `${F} matches:anycase ${Q(shaped[0])}`
        case 'endswith':
          return isMulti
            ? `${F} matches:anycase ${LIST()}`
            : `${F} matches:anycase ${Q(shaped[0])}`

        // comparisons: keep quoted per your global rule
        case 'lt':
          return `${F} < ${Q(shaped[0])}`
        case 'lte':
          return `${F} <= ${Q(shaped[0])}`
        case 'gt':
          return `${F} > ${Q(shaped[0])}`
        case 'gte':
          return `${F} >= ${Q(shaped[0])}`

        // empties / nulls (best-effort; adjust if you need specific S1 functions)
        case 'isnull':
          return NOT(`${F} = *`)
        case 'isnotnull':
          return `${F} = *`
        case 'isempty':
          return NOT(`${F} = *`)
        case 'isnotempty':
          return `${F} = *`

        default:
          // sane default = equality
          return isMulti ? `${F} = ${LIST()}` : `${F} = ${Q(shaped[0])}`
      }
    },
  })

  public readonly tuningText =
    "Append tuning logic to the end of the base hunt logic, typically after the last '| filter ...' or before '| comp ...'. NOTE: If a '| columns ...' declaration is utilized at the beginning of the hunt logic, it may need to be removed to include all fields."

  public override readonly datasourceMap = s1PowerQueryMap
  public readonly dataDisplayTemplate = s1PowerQueryDataDisplayTemplate

  /**
   * Emit the SentinelOne PowerQuery datasource prelude — the complete
   * event.category/event.type clause stored in events[0].
   */
  public emitS1PowerQueryDatasource(ds: NormalizedDataSource | undefined): string {
    return ds?.events?.[0] ?? ''
  }

  /**
   * Emit prelude merged into the first | filter line + tuning body + DataDisplay clause.
   * e.g. | filter event.category = 'file' AND event.type = 'File Creation' AND tgt.file.path matches:anycase 'malware.exe$'
   */
  public readonly renderSentinelOnePowerQuery = wrapRendererWithDataDisplay(
    wrapRendererWithDatasource(
      this.renderTuning,
      this.toolName,
      (ds) => this.emitS1PowerQueryDatasource(ds),
      joinWithPipeBlock,
      this.tuningText,
    ),
    this.toolName,
    s1PowerQueryDataDisplayTemplate,
  )
}
